import { ReactNode } from 'react'

import {
  ClientCompanyDetails,
  ClientCompanyDetailsDTO,
  ClientRelatedCompanyDTO,
  ContractFeeDetailsDTO,
} from '@/models/back-office-pages/clients'
import { DebtorDetailsDTO } from '@/models/back-office-pages/debtors'
import {
  AddKycIdentityVariables,
  AmlRisk,
  ApprovalLogsDTO,
  BalanceSheetDTO,
  BalanceSheetEntryDTO,
  BalanceYearDTO,
  EditKycIdentityVariables,
  FactoringProduct,
  FundingLine,
  KycIdentityDTO,
  ProfitLossDTO,
  ProfitLossEntryDTO,
  RatiosDTO,
  ReportType,
  RiskRatioEntryDTO,
  RiskRatiosDTO,
} from '@/models/back-office-pages/prospects'
import {
  AdministrationTypes,
  CommentType,
  Currency,
  FromToFilterInput,
  MinMaxFilterInput,
  ReportFileDTOInput,
  ReportStatus,
  RepresentativeCompanyDTO,
  RepresentativeDTO,
  RiskScoresDTO,
} from '@/models/common'
import { Direction } from '@/models/table'

export enum AnnualReviewStatus {
  APPROVED = 'APPROVED',
  NEW = 'NEW',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  NEEDS_CLARIFICATION = 'NEEDS_CLARIFICATION',
  ARCHIVED = 'ARCHIVED',
}

export enum AnnualReviewFilterStatus {
  ALL = 'ALL',
  ACCEPTED = 'ACCEPTED',
  NEW = 'NEW',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  NEEDS_CLARIFICATION = 'NEEDS_CLARIFICATION',
  ARCHIVED = 'ARCHIVED',
}

export interface AnnualReviewDTO {
  companyCountry: string
  companyName: string
  createdInFab: Date
  financingLimit: number
  dunId: string
  fabClientId: string
  id: string
  insuredLimit: number
  isGAmountUsed: boolean
  naceCode: string
  naceDescription: string
  registrationCode: string
  riskAssignee: string
  status: AnnualReviewStatus
  usedLimit: number
  contractEndDate: Date
  contractStartDate: Date
  approvalDate: Date
  amlRisk: AmlRisk

  [key: string]: unknown
}

export interface Page_AnnualReviewDTO {
  content: Array<AnnualReviewDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface AnnualReviewData {
  annualReviews: Page_AnnualReviewDTO
}

export interface AnnualReviewVariables {
  clientId?: string
  contractStartDate?: FromToFilterInput
  companyName?: string
  contractEndDate?: FromToFilterInput
  pageSize: number
  tableFilteringStatus: AnnualReviewFilterStatus
  pageNum: number
  financingLimit?: MinMaxFilterInput
  riskAssignee?: string
  sortDirection: Direction
  usedLimit?: MinMaxFilterInput
  sortField: string
  registrationCode?: string
  companyCountry?: string
  status?: Array<AnnualReviewStatus>
  amlRisk?: Array<AmlRisk>
}

export interface AnnualReviewImportResponseDTO {
  count: number
}

export interface UploadAnnualReviewsData {
  uploadAnnualReviews: AnnualReviewImportResponseDTO
}

export interface UploadAnnualReviewsVariables {
  content: string
}

export interface UploadAnnualReviewsByIdData {
  uploadAnnualReviewsById: AnnualReviewImportResponseDTO
}

export interface UploadAnnualReviewsByIdVariables {
  id: string
}

export interface AnnualReviewReportResponseDTO {
  reportId: string
}

export interface RequestGenerateAnnualReviewsReportData {
  requestAnnualReviewsReport: AnnualReviewReportResponseDTO
}

export interface RequestGenerateAnnualReviewsReportVariables {
  contractStartDate?: FromToFilterInput
  companyName?: string
  contractEndDate?: FromToFilterInput
  tableFilteringStatus: AnnualReviewFilterStatus
  financingLimit?: MinMaxFilterInput
  riskAssignee?: string
  usedLimit?: MinMaxFilterInput
  registrationCode?: string
  companyCountry?: Array<string>
  status?: Array<AnnualReviewStatus>
  amlRisk?: Array<AmlRisk>
}

export interface AnnualReviewReportDTO {
  documentId: string
  fileName: string
  createdOn: Date
  status: ReportStatus
  uploader: string
}

interface Page_AnnualReviewReportDTO {
  content: AnnualReviewReportDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface AnnualReviewsReportsData {
  annualReviewsReportList: Page_AnnualReviewReportDTO
}

export interface AnnualReviewsReportsVariables {
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
  createdOn?: FromToFilterInput
}

export interface CancelAnnualReviewReportData {
  cancelAnnualReviewReport: AnnualReviewReportResponseDTO
}

export interface CancelAnnualReviewReportVariables {
  reportId: string
}

export interface DownloadAnnualReviewListReportData {
  downloadAnnualReviewListReport: AnnualReviewReportFileDTO
}

export interface DownloadAnnualReviewListReportVariables {
  documentId: string
}

export interface AnnualReviewContractDTO {
  companyName: string
  contractEndDate: Date
  contractStartDate: Date
  duns: string
  id: string
  registrationNumber: string
  searchText: string
  sourcingAgent: string
}

export interface SearchCompanyData {
  searchAnnualReviewClients: Array<AnnualReviewContractDTO>
}

export interface SearchCompanyVariables {
  searchText: string
}

export enum UploadType {
  BY_FILE = 'BY_FILE',
  BY_ID = 'BY_ID',
}

export enum AnnualReviewFieldNames {
  FILE = 'file',
  UPLOAD_TYPE = 'uploadType',
  ID = 'id',
}

export interface AnnualReviewCustomerSectorDTO {
  naceCode: string
  naceDescription: string
}

export interface AnnualReviewFinancingLimitDTO {
  financingLimit: number
  suggestedFinancingLimit: number
  usedLimit: number
}

export enum AnnualReviewRejectReason {
  CLIENT_COMPETITOR = 'CLIENT_COMPETITOR',
  CLIENT_DEBTOR_WILL_NOT_SIGN = 'CLIENT_DEBTOR_WILL_NOT_SIGN',
  CLIENT_INFO_DOCUMENTS_MISSING = 'CLIENT_INFO_DOCUMENTS_MISSING',
  CLIENT_INSURANCE_LIMIT = 'CLIENT_INSURANCE_LIMIT',
  CLIENT_MISSING_DOCUMENTS = 'CLIENT_MISSING_DOCUMENTS',
  CLIENT_NEEDS_MORE_FUNDING_THAN_OFFERED = 'CLIENT_NEEDS_MORE_FUNDING_THAN_OFFERED',
  CLIENT_NEEDS_RESOLVED = 'CLIENT_NEEDS_RESOLVED',
  CLIENT_NON_RESPONSIVE = 'CLIENT_NON_RESPONSIVE',
  CLIENT_PAID_BY_DEBTOR = 'CLIENT_PAID_BY_DEBTOR',
  CLIENT_PRICING = 'CLIENT_PRICING',
  CLIENT_SHOPPING = 'CLIENT_SHOPPING',
  COMPETITION_SIGNED_WITH_COMPETITOR = 'COMPETITION_SIGNED_WITH_COMPETITOR',
  DD_LOST_TO_PLEDGE = 'DD_LOST_TO_PLEDGE',
  DD_REJECTED_BY_RISK = 'DD_REJECTED_BY_RISK',
  DELAYS_NON_RESPONSIVE_CLIENT = 'DELAYS_NON_RESPONSIVE_CLIENT',
  NO_FIT_CONDITIONS = 'NO_FIT_CONDITIONS',
}

export interface AnnualReviewAssigneeDTO {
  riskAssignee: string
  salesAssignee: string
}

export interface AnnualReviewAgreementDTO {
  clientLastInvoiceFundedAt: Date
  contractEndDate: Date
  contractStartDate: Date
}

export interface AnnualReviewDunsDetailsDTO {
  city: string
  country: string
  dunReportCreated: Date
  dunsId: string
  hooversIndustryCode: string
  naceIndustryCode: string
  numberOfEmployees: number
  postalCode: string
  rawData: string
  registrationNumber: string
  standardIndustryCode: string
  startDate: string
  streetAddressLine1: string
  streetAddressLine2: string
  vatNumber: string
}

export interface AnnualReviewDetailsDTO {
  amlRisk: AmlRisk
  companyName: string
  approval: AnnualReviewApprovalDTO
  status: AnnualReviewStatus
  assignees: AnnualReviewAssigneeDTO
  customerSector: AnnualReviewCustomerSectorDTO
  financingLimits: AnnualReviewFinancingLimitDTO
  financingLimitsPln: AnnualReviewFinancingLimitDTO
  financialRatiosEntries: Array<RatiosDTO>
  riskScoresEntries: Array<RiskScoresDTO>
  logs: Array<ApprovalLogsDTO>
  agreement: AnnualReviewAgreementDTO
  turnover: AnnualReviewTurnoverDTO
  dunsDetails: AnnualReviewDunsDetailsDTO
}

export interface AnnualReviewDetailsData {
  loadAnnualReviewDetails: AnnualReviewDetailsDTO
}

export interface UpdateAnnualReviewRatiosVariables extends RatiosDTO {
  annualReviewId: string
}

export interface UpdateAnnualReviewRatiosData {
  updateAnnualReviewFinancialRatios: RatiosDTO
}

export interface UpdateAnnualReviewDunsDetailsData {
  refreshAnnualReviewsDunsDetails: boolean
}

export interface UpdateAnnualReviewDunsDetailsVariables {
  id: string
}

export interface AnnualReviewDebtorBehaviorDTO {
  fundingLine: string
  limitExpirationDate: Date
  outstandingExposureAmount: number
  paidByDebtorRatio: number
  paidDebtorVsClosedInvoicesAmount: number // old version
  outstandingOverdueExposureAmount: number
  averageOverdueDaysOpened: number
  maxOverdueClosed: number
  maxOverdueDaysClosed: number // old version
  maxOverdueOpen: number
  maxOverdueDaysOpened: number // old version
  factorableAmount: number
  totalFinanced: number
  closedAverageTerm: number
  averageUsage: number
  totalLate: number
  totalOutstanding: number
  totalWithRiskMarks: number
}

export enum AnnualReviewDebtorProductType {
  INSURED = 'INSURED',
  MIXED = 'MIXED',
  RECOURSE = 'RECOURSE',
  UNINSURED = 'UNINSURED',
}

export interface AnnualReviewDebtorProductAndLimitDTO {
  factoringProductType: AnnualReviewDebtorProductType
  factoringProductTypePrevious: AnnualReviewDebtorProductType
  insuredCreditLimit: number
  insuredCreditLimitPrevious: number
  recourseCreditLimit: number
  recourseCreditLimitPrevious: number
  totalCreditLimit: number
  totalCreditLimitPrevious: number
  uninsuredCreditLimit: number
  uninsuredCreditLimitPrevious: number
}

export enum ScorePeriod {
  CURRENT = 'CURRENT',
  PREVIOUS = 'PREVIOUS',
}

export enum AnnualReviewApprovalInitiationError {
  NOT_ALL_DEBTORS_APPROVED = 'NOT_ALL_DEBTORS_APPROVED',
  RISK_QA_NOT_FILLED = 'RISK_QA_NOT_FILLED',
}

export interface AnnualReviewApprovalDTO {
  approvalComment: string
  approvalDate: Date
  approver: string
  canApprove: boolean
  approvalInitiationErrors: Array<AnnualReviewApprovalInitiationError>
}

export interface Map_Integer_DoubleScalar {
  [key: number]: number
}

export interface AnnualReviewTurnoverDTO {
  turnovers: Map_Integer_DoubleScalar
  years: Array<number>
  yoyChanges: Map_Integer_DoubleScalar
}

export interface AnnualReviewDebtorDunsDetailsDTO {
  city: string
  country: string
  dunReportCreated: Date
  dunsId: string
  hooversIndustryCode: string
  naceIndustryCode: string
  numberOfEmployees: number
  postalCode: string
  rawData: string
  registrationNumber: string
  standardIndustryCode: string
  startDate: string
  streetAddressLine1: string
  streetAddressLine2: string
  vatNumber: string
}

export interface AnnualReviewDebtorDetailsDTO {
  annualReviewCompanyName: string
  annualReviewId: string
  behaviorData: AnnualReviewDebtorBehaviorDTO
  companyCountryCode: string
  companyName: string
  approval: AnnualReviewApprovalDTO
  financialRatiosEntries: [RatiosDTO]
  hasStoplistDebtorCompanyMatch: boolean
  hasStoplistDebtorContactMatch: boolean
  id: string
  productAndLimitData: AnnualReviewDebtorProductAndLimitDTO
  riskScoresEntries: Array<RiskScoresDTO>
  status: AnnualReviewStatus
  dunsDetails: AnnualReviewDebtorDunsDetailsDTO
}

export interface AnnualReviewDebtorDetailsData {
  annualReviewDebtorDetails: AnnualReviewDebtorDetailsDTO
}

export interface AnnualReviewDebtorDetailsVariables {
  debtorId: string
}

export interface UpdateAnnualReviewRiskScoresData {
  updateAnnualReviewRiscScores: AnnualReviewDetailsDTO
}

export interface UpdateAnnualReviewRiskScoresVariables {
  id: string
}

export interface UpdateAnnualReviewDebtorDunsDetailsData {
  refreshAnnualReviewDebtorsDunsDetails: boolean
}

export interface UpdateAnnualReviewDebtorDunsDetailsVariables {
  id: string
}

export enum YesNoNaChoice {
  NA = 'NA',
  NO = 'NO',
  YES = 'YES',
}

export interface AnnualReviewRiskQuestionnaireDTO {
  actualDelaysInTaxes: boolean
  actualDelaysInTaxesComments: string
  hitInStoplistChoice: YesNoNaChoice
  hitInStoplistComments: string
  hitPDClassChanges: boolean
  hitPDClassChangesComments: string
  hitChangesInShareholders: boolean
  hitChangesInShareholdersComments: string
  hitInUboLegalRepresentativeIdValidity: boolean
  hitInUboLegalRepresentativeIdValidityComments: string
  hitHighRiskKycScreening: boolean
  hitHighRiskKycScreeningComments: string
  hitForVatValidity: boolean
  hitForVatValidityComments: string
  hitMissingFinancials: boolean
  hitMissingFinancialsComments: string
  hitForRiskOfferComplianceComments: string
  hitForRiskOfferCompliance: boolean
  hitForAddressChangesComments: string
  hitForAddressChanges: boolean
  hitInPaymentHistoryComments: string
  hitInPaymentHistory: boolean
  hitClientLimitConcentration: boolean
  hitClientLimitConcentrationComments: string
  hitClientCompanyFound: boolean
  hitClientCompanyFoundComments: string
  hitClientRepresentativeFound: boolean
  hitClientRepresentativeFoundComments: string
  id: string
  summary: string
  representativeCompanies: Array<RepresentativeCompanyDTO>
  representatives: Array<RepresentativeDTO>
  relatedCompanies: Array<ClientRelatedCompanyDTO>
}

export interface AnnualReviewRiskQuestionnaireData {
  annualReviewRiskQuestionnaires: AnnualReviewRiskQuestionnaireDTO
}

export interface AnnualReviewRiskQuestionnaireVariables {
  id: string
}

export interface UpdateAnnualReviewRiskQuestionnaireData {
  updateAnnualReviewRiskQuestionnaires: AnnualReviewRiskQuestionnaireDTO
}

export interface UpdateAnnualReviewRiskQuestionnaireVariables extends AnnualReviewRiskQuestionnaireDTO {}

export interface UpdateAnnualReviewRepresentativesData {
  updateFabClientRepresentatives: AnnualReviewRiskQuestionnaireDTO
}

export interface UpdateAnnualReviewRepresentativesVariables {
  id: string
  relatedCompanies?: string[]
  representativeCompanies?: string[]
  representatives?: Array<RepresentativeDTO>
}

export enum AnnualReviewRiskQuestionnaireFieldNames {
  ACTUAL_DELAY_IN_TAXES = 'actualDelaysInTaxes',
  ACTUAL_DELAY_IN_TAXES_COMMENTS = 'actualDelaysInTaxesComments',
  HIT_IN_STOPLIST = 'hitInStoplistChoice',
  HIT_IN_STOPLIST_COMMENTS = 'hitInStoplistComments',
  HIT_PD_CLASS_CHANGES = 'hitPDClassChanges',
  HIT_PD_CLASS_CHANGES_COMMENTS = 'hitPDClassChangesComments',
  HIT_CHANGES_IN_SHAREHOLDERS = 'hitChangesInShareholders',
  HIT_CHANGES_IN_SHAREHOLDERS_COMMENTS = 'hitChangesInShareholdersComments',
  HIT_IN_UBO_ID_VALIDITY = 'hitInUboLegalRepresentativeIdValidity',
  HIT_IN_UBO_ID_VALIDITY_COMMENTS = 'hitInUboLegalRepresentativeIdValidityComments',
  HIT_HIGH_RISK_KYC_SCREENING = 'hitHighRiskKycScreening',
  HIT_HIGH_RISK_KYC_SCREENING_COMMENTS = 'hitHighRiskKycScreeningComments',
  HIT_FOR_VAT_VALIDITY = 'hitForVatValidity',
  HIT_FOR_VAT_VALIDITY_COMMENTS = 'hitForVatValidityComments',
  HIT_MISSING_FINANCIALS = 'hitMissingFinancials',
  HIT_IN_MISSING_FINANCIALS_COMMENTS = 'hitMissingFinancialsComments',
  HIT_FOR_RISK_OFFER_COMPLIANCE = 'hitForRiskOfferCompliance',
  HIT_FOR_RISK_OFFER_COMPLIANCE_COMMENTS = 'hitForRiskOfferComplianceComments',
  HIT_FOR_ADDRESS_CHANGES = 'hitForAddressChanges',
  HIT_FOR_ADDRESS_CHANGES_COMMENTS = 'hitForAddressChangesComments',
  HIT_IN_PAYMENT_HISTORY = 'hitInPaymentHistory',
  HIT_IN_PAYMENT_HISTORY_COMMENTS = 'hitInPaymentHistoryComments',
  HIT_CLIENT_LIMIT_CONCENTRATION = 'hitClientLimitConcentration',
  HIT_CLIENT_LIMIT_CONCENTRATION_COMMENTS = 'hitClientLimitConcentrationComments',
  HIT_CLIENT_COMPANY_FOUND = 'hitClientCompanyFound',
  HIT_CLIENT_COMPANY_FOUND_COMMENTS = 'hitClientCompanyFoundComments',
  HIT_CLIENT_REPRESENTATIVE_FOUND = 'hitClientRepresentativeFound',
  HIT_CLIENT_REPRESENTATIVE_FOUND_COMMENTS = 'hitClientRepresentativeFoundComments',
}

export interface UpdateAnnualReviewSummaryData {
  updateAnnualReviewRiskQuestionnairesSummary: AnnualReviewRiskQuestionnaireDTO
}

export interface UpdateAnnualReviewRiskSummaryVariables {
  id: string
  summary: string
}

export interface AnnualReviewBalanceSheetData {
  annualReviewBalanceSheet: BalanceSheetDTO
}

export interface AnnualReviewBalanceSheetVariables {
  annualReviewId: string
}

export interface AnnualReviewBalanceSheetEntryData {
  annualReviewBalanceSheetEntry: BalanceSheetEntryDTO
}

export interface AnnualReviewBalanceSheetEntryVariables {
  annualReviewId: string
  year: string
}

export interface SaveAnnualReviewBalanceSheetData {
  saveAnnualReviewsBalanceSheet: BalanceSheetEntryDTO
}

export interface SaveAnnualReviewBalanceSheetVariables extends BalanceSheetEntryDTO {
  annualReviewId: string
}

export interface AnnualReviewBalanceSheetYearsData {
  annualReviewBalanceSheetYears: Array<BalanceYearDTO>
}

export interface AnnualReviewBalanceSheetYearsVariables {
  annualReviewId: string
}

export interface DeleteAnnualReviewBalanceSheetData {
  deleteAnnualReviewBalanceSheet: BalanceYearDTO
}

export interface DeleteAnnualReviewBalanceSheetVariables {
  annualReviewId: string
  year: string
}

export interface SaveAnnualReviewProfitLossData {
  saveAnnualReviewProfitLoss: ProfitLossEntryDTO
}

export interface SaveAnnualReviewProfitLossVariables extends ProfitLossEntryDTO {
  annualReviewId: string
}

export interface AnnualReviewProfitLossEntryData {
  annualReviewProfitLossEntry: ProfitLossEntryDTO
}

export interface AnnualReviewProfitLossEntryVariables {
  annualReviewId: string
  year: string
}

export interface AnnualReviewProfitLossYearsData {
  annualReviewProfitLossYears: Array<BalanceYearDTO>
}

export interface AnnualReviewProfitLossYearsVariables {
  annualReviewId: string
}

export interface DeleteAnnualReviewProfitLossData {
  deleteAnnualReviewProfitLoss: BalanceYearDTO
}

export interface DeleteAnnualReviewProfitLossVariables {
  annualReviewId: string
  year: string
}

export interface AnnualReviewProfitLossData {
  annualReviewProfitLoss: ProfitLossDTO
}

export interface AnnualReviewProfitLossVariables {
  annualReviewId: string
}

export interface AnnualReviewRiskRatiosData {
  annualReviewRiskRatios: RiskRatiosDTO
}

export interface AnnualReviewRiskRatiosVariables {
  annualReviewId: string
}

export interface AnnualReviewRiskRatiosYearsData {
  annualReviewRiskRatiosYears: Array<BalanceYearDTO>
}

export interface AnnualReviewRiskRatiosYearsVariables {
  annualReviewId: string
}

export interface AnnualReviewRiskRatiosEntryData {
  annualReviewRiskRatiosEntry: RiskRatioEntryDTO
}

export interface AnnualReviewRiskRatiosEntryVariables {
  annualReviewId: string
  year: string
}

export interface SaveAnnualReviewRiskRatiosData {
  saveAnnualReviewRiskRatios: RiskRatioEntryDTO
}

export interface SaveAnnualReviewRiskRatiosVariables extends RiskRatioEntryDTO {
  annualReviewId: string
}

export interface DeleteAnnualReviewRiskRatiosData {
  deleteAnnualReviewRiskRatios: BalanceYearDTO
}

export interface DeleteAnnualReviewRiskRatiosVariables {
  annualReviewId: string
  year: string
}

export interface UpdateAnnualReviewAssigneesData {
  updateAnnualReviewsAssignees: AnnualReviewDetailsDTO
}

export interface UpdateAnnualReviewAssigneesVariables {
  id: string
  riskAssignee: string
  salesAssignee: string
}

export interface AnnualReviewReportFileDTO {
  content: string
  documentId: string
  fileName: string
  annualReviewId: string
  description: string
  reportCreatedAt: string
  reportType: ReportType
  sendReportToReceivers: boolean
  uploader: string
}

export interface Page_AnnualReviewReportFileDTO {
  content: Array<AnnualReviewReportFileDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface AnnualReviewReportsData {
  annualReviewReports: Page_AnnualReviewReportFileDTO
}

export interface AnnualReviewReportsVariables {
  annualReviewId: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface DownloadAnnualReviewReportData {
  downloadAnnualReviewReport: AnnualReviewReportFileDTO
}

export interface DownloadAnnualReviewReportVariables {
  documentId: string
}

export interface UploadAnnualReviewReportData {
  uploadAnnualReviewReport: string
}

export interface UploadAnnualReviewReportVariables {
  reportType: ReportType
  annualReviewId?: string
  sendReportToReceivers: boolean
  file: ReportFileDTOInput
  reportCreatedAt: string
  description?: string
}

export interface UploadAnnualReviewReportFormValues {
  reportType: ReportType
  annualReviewId?: string
  sendReportToReceivers: boolean
  files: ReportFileDTOInput[]
  reportCreatedAt: string
  description?: string
}

export interface AnnualReviewDebtorDTO {
  companyName: string
  country: string
  creditLimit: number
  hits: number
  id: string
  limitExpirationDate: Date
  registrationCode: string
  status: AnnualReviewStatus
  stoplistMatch: boolean
  usedLimit: number

  [key: string]: unknown
}

export interface Page_AnnualReviewDebtorDTO {
  content: [AnnualReviewDebtorDTO]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface AnnualReviewDebtorsData {
  annualReviewDebtors: Page_AnnualReviewDebtorDTO
}

export interface AnnualReviewDebtorsVariables {
  annualReviewId: string
  companyName?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface ArchiveAnnualReviewDebtorsData {
  archiveAnnualReviewDebtors: boolean
}

export interface ArchiveAnnualReviewDebtorsVariables {
  ids: Array<string>
}

export interface ResetToNewAnnualReviewDebtorsData {
  resetToNewAnnualReviewDebtors: boolean
}

export interface ResetToNewAnnualReviewDebtorsVariables {
  ids: Array<string>
}

export interface AddAnnualReviewDebtorData {
  addAnnualReviewDebtors: Array<AnnualReviewAvailableDebtorDTO>
}

export interface AddAnnualReviewDebtorVariables {
  annualReviewId: string
  debtors: Array<AnnualReviewAvailableDebtorDTO>
}

export enum AddAnnualReviewDebtorFieldNames {
  DEBTORS = 'debtors',
}

export interface AnnualReviewAvailableDebtorDTO {
  companyName: string
  dunId: string
  id: string
  __typename: string
}

export interface AnnualReviewAvailableDebtorsData {
  annualReviewAvailableDebtors: AnnualReviewAvailableDebtorDTO[]
}

export interface AnnualReviewAvailableDebtorsVariables {
  annualReviewId: string
}

export interface UpdateAnnualReviewDebtorLimitsData {
  updateAnnualReviewDebtorLimits: AnnualReviewDebtorDetailsDTO
}

export interface UpdateAnnualReviewDebtorLimitsVariables {
  factoringProductType: AnnualReviewDebtorProductType
  recourseCreditLimit: number
  insuredCreditLimit: number
  uninsuredCreditLimit: number
  debtorId: string
}

export interface AnnualReviewRiskDebtorQuestionnaireDTO {
  hitInStoplist: boolean
  hitInStoplistComments: string
  hitPDClassChanges: boolean
  hitPDClassChangesComments: string
  hitInPaymentHistory: boolean
  hitInPaymentHistoryComments: string
  hitInInsuranceLimit: boolean
  hitInInsuranceLimitComments: string
  hitForDebtorsCompanyFromOndatoKyc: boolean
  hitForDebtorsCompanyFromOndatoKycComments: string
  hitForDebtorsPersonsFromOndatoKyc: boolean
  hitForDebtorsPersonsFromOndatoKycComments: string
  hitDebtorLimitConcentration: boolean
  hitDebtorLimitConcentrationComments: string
  hitDebtorCompanyFound: boolean
  hitDebtorCompanyFoundComments: string
  hitDebtorRepresentativeFound: boolean
  hitDebtorRepresentativeFoundComments: string
  id: string
  summary: string
  representativeCompanies: Array<RepresentativeCompanyDTO>
  representatives: Array<RepresentativeDTO>
  relatedCompanies: Array<ClientRelatedCompanyDTO>
}

export interface AnnualReviewDebtorRiskQuestionnaireData {
  annualReviewDebtorRiskQuestionnaires: AnnualReviewRiskDebtorQuestionnaireDTO
}

export interface AnnualReviewDebtorRiskQuestionnaireVariables {
  id: string
}

export interface UpdateAnnualReviewDebtorRiskQuestionnaireData {
  updateAnnualReviewDebtorRiskQuestionnaires: AnnualReviewRiskDebtorQuestionnaireDTO
}

export interface UpdateAnnualReviewDebtorRiskQuestionnaireVariables extends AnnualReviewRiskDebtorQuestionnaireDTO {}

export interface UpdateAnnualReviewDebtorRepresentativesData {
  updateFabDebtorRepresentatives: AnnualReviewRiskDebtorQuestionnaireDTO
}

export interface UpdateAnnualReviewDebtorRepresentativesVariables {
  id: string
  relatedCompanies?: string[]
  representativeCompanies?: string[]
  representatives?: Array<RepresentativeDTO>
}

export enum AnnualReviewDebtorRiskQuestionnaireFieldNames {
  HIT_IN_STOPLIST = 'hitInStoplist',
  HIT_IN_STOPLIST_COMMENTS = 'hitInStoplistComments',
  HIT_PD_CLASS_CHANGES = 'hitPDClassChanges',
  HIT_PD_CLASS_CHANGES_COMMENTS = 'hitPDClassChangesComments',
  HIT_IN_PAYMENT_HISTORY = 'hitInPaymentHistory',
  HIT_IN_PAYMENT_HISTORY_COMMENTS = 'hitInPaymentHistoryComments',
  HIT_IN_INSURANCE_LIMIT = 'hitInInsuranceLimit',
  HIT_IN_INSURANCE_LIMIT_COMMENTS = 'hitInInsuranceLimitComments',
  PERSON_HIT_IN_ONDATO = 'hitForDebtorsPersonsFromOndatoKyc',
  PERSON_HIT_IN_ONDATO_COMMENT = 'hitForDebtorsPersonsFromOndatoKycComments',
  COMPANY_HIT_IN_ONDATO = 'hitForDebtorsCompanyFromOndatoKyc',
  COMPANY_HIT_IN_ONDATO_COMMENT = 'hitForDebtorsCompanyFromOndatoKycComments',
  HIT_DEBTOR_LIMIT_CONCENTRATION = 'hitDebtorLimitConcentration',
  HIT_DEBTOR_LIMIT_CONCENTRATION_COMMENTS = 'hitDebtorLimitConcentrationComments',
  HIT_DEBTOR_COMPANY_FOUND = 'hitDebtorCompanyFound',
  HIT_DEBTOR_COMPANY_FOUND_COMMENTS = 'hitDebtorCompanyFoundComments',
  HIT_DEBTOR_REPRESENTATIVE_FOUND = 'hitDebtorRepresentativeFound',
  HIT_DEBTOR_REPRESENTATIVE_FOUND_COMMENTS = 'hitDebtorRepresentativeFoundComments',
}

export interface UpdateAnnualReviewDebtorSummaryData {
  updateAnnualReviewDebtorRiskQuestionnairesSummary: AnnualReviewRiskDebtorQuestionnaireDTO
}

export interface UpdateAnnualReviewRiskDebtorSummaryVariables {
  id: string
  summary: string
}

export interface AnnualReviewDebtorBalanceSheetData {
  annualReviewDebtorsBalanceSheet: BalanceSheetDTO
}

export interface AnnualReviewDebtorBalanceSheetVariables {
  debtorId: string
}

export interface AnnualReviewDebtorBalanceSheetEntryData {
  annualReviewDebtorsBalanceSheetEntry: BalanceSheetEntryDTO
}

export interface AnnualReviewDebtorBalanceSheetEntryVariables {
  debtorId: string
  year: string
}

export interface SaveAnnualReviewDebtorBalanceSheetData {
  saveAnnualReviewDebtorsBalanceSheet: BalanceSheetEntryDTO
}

export interface SaveAnnualReviewDebtorBalanceSheetVariables extends BalanceSheetEntryDTO {
  debtorId: string
}

export interface AnnualReviewDebtorBalanceSheetYearsData {
  annualReviewDebtorsBalanceSheetYears: Array<BalanceYearDTO>
}

export interface AnnualReviewDebtorBalanceSheetYearsVariables {
  debtorId: string
}

export interface DeleteAnnualReviewDebtorBalanceSheetData {
  deleteAnnualReviewDebtorsBalanceSheet: BalanceYearDTO
}

export interface DeleteAnnualReviewDebtorBalanceSheetVariables {
  debtorId: string
  year: string
}

export interface SaveAnnualReviewDebtorProfitLossData {
  saveAnnualReviewDebtorsProfitLoss: ProfitLossEntryDTO
}

export interface SaveAnnualReviewDebtorProfitLossVariables extends ProfitLossEntryDTO {
  debtorId: string
}

export interface AnnualReviewDebtorProfitLossEntryData {
  annualReviewDebtorsProfitLossEntry: ProfitLossEntryDTO
}

export interface AnnualReviewDebtorProfitLossEntryVariables {
  debtorId: string
  year: string
}

export interface AnnualReviewDebtorProfitLossYearsData {
  debtorsAnnualReviewProfitLossYears: Array<BalanceYearDTO>
}

export interface AnnualReviewDebtorProfitLossYearsVariables {
  debtorId: string
}

export interface DeleteAnnualReviewDebtorProfitLossData {
  deleteAnnualReviewDebtorsProfitLoss: BalanceYearDTO
}

export interface DeleteAnnualReviewDebtorProfitLossVariables {
  debtorId: string
  year: string
}

export interface AnnualReviewDebtorProfitLossData {
  annualReviewDebtorsProfitLoss: ProfitLossDTO
}

export interface AnnualReviewDebtorProfitLossVariables {
  debtorId: string
}

export interface AnnualReviewReportFileDTO {
  content: string
  description: string
  annualReviewId: string
  fileName: string
  prospectId: string
  reportCreatedAt: string
  reportType: ReportType
  sendReportToReceivers: boolean
  uploader: string
}

export interface Page_AnnualReviewReportFileDTO {
  content: Array<AnnualReviewReportFileDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface AnnualReviewDebtorReportsData {
  annualReviewDebtorReports: Page_AnnualReviewReportFileDTO
}

export interface AnnualReviewDebtorReportsVariables {
  annualReviewDebtorId: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface DownloadAnnualReviewDebtorReportData {
  downloadAnnualReviewDebtorReport: AnnualReviewReportFileDTO
}

export interface DownloadAnnualReviewDebtorReportVariables {
  documentId: string
}

export interface UploadAnnualReviewDebtorReportData {
  uploadAnnualReviewDebtorReport: string
}

export interface UploadAnnualReviewDebtorReportVariables {
  reportType: ReportType
  annualReviewDebtorId?: string
  sendReportToReceivers: boolean
  file: ReportFileDTOInput
  reportCreatedAt: string
  description?: string
  index: number
}

export interface UploadAnnualReviewDebtorFormValues {
  reportType: ReportType
  annualReviewDebtorId?: string
  sendReportToReceivers: boolean
  files: ReportFileDTOInput[]
  reportCreatedAt: string
  description?: string
}

export interface ApproveAnnualReviewData {
  approveAnnualReview: AnnualReviewDetailsDTO
}

export interface ApproveAnnualReviewVariables {
  annualReviewId: string
  comment: string
}

export interface RejectAnnualReviewData {
  rejectAnnualReview: AnnualReviewDetailsDTO
}

export interface RejectAnnualReviewVariables {
  annualReviewId: string
  comment: string
}

export interface StartClarificationAnnualReviewData {
  startClarificationAnnualReview: AnnualReviewDetailsDTO
}

export interface StartClarificationAnnualReviewVariables {
  annualReviewId: string
  comment: string
}

export interface FinishClarificationAnnualReviewData {
  finishClarificationAnnualReview: AnnualReviewDetailsDTO
}

export interface FinishClarificationAnnualReviewVariables {
  annualReviewId: string
  comment: string
}

export interface ApproveAnnualReviewDebtorData {
  approveAnnualReviewDebtor: AnnualReviewDebtorDetailsDTO
}

export interface ApproveAnnualReviewDebtorVariables {
  debtorId: string
  comment: string
}

export interface RejectAnnualReviewDebtorData {
  rejectAnnualReviewDebtor: AnnualReviewDebtorDetailsDTO
}

export interface RejectAnnualReviewDebtorVariables {
  debtorId: string
  comment: string
}

export interface StartClarificationAnnualReviewDebtorData {
  startClarificationAnnualReviewDebtor: AnnualReviewDebtorDetailsDTO
}

export interface StartClarificationAnnualReviewDebtorVariables {
  debtorId: string
  comment: string
}

export interface FinishClarificationAnnualReviewDebtorData {
  finishClarificationAnnualReviewDebtor: AnnualReviewDebtorDetailsDTO
}

export interface FinishClarificationAnnualReviewDebtorVariables {
  debtorId: string
  comment: string
}

export enum KycIdentitySource {
  ADDED_MANUALLY = 'ADDED_MANUALLY',
  FROM_REPORT = 'FROM_REPORT',
}

export interface AnnualReviewKycIdentityDTO extends KycIdentityDTO {
  addedFrom: KycIdentitySource
  annualReviewId: string
  roleFromReport: string
  amountOfShares: number
  appointmentDate: Date
  recentShareholder: boolean

  [key: string]: string | ReactNode | Date
}

export interface AnnualReviewKycIdentitiesData {
  annualReviewKycIdentities: Array<AnnualReviewKycIdentityDTO>
}

export interface AnnualReviewKycIdentitiesVariables {
  annualReviewId: string
}

export interface AnnualReviewKycIdentityData {
  annualReviewKycIdentity: AnnualReviewKycIdentityDTO
}

export interface AnnualReviewKycIdentityVariables {
  id: string
}

export interface CheckAnnualReviewKycIdentityData {
  checkAnnualReviewKycStatus: Array<AnnualReviewKycIdentityDTO>
}

export interface CheckAnnualReviewKycIdentityVariables {
  id: string
}

export interface StartAnnualReviewKycData {
  startAnnualReviewKyc: Array<AnnualReviewKycIdentityDTO>
}

export interface StartAnnualReviewKycVariables {
  id: string
}

export interface AddAnnualReviewKycIdentityData {
  addAnnualReviewKycIdentity: AnnualReviewKycIdentityDTO
}

export interface AddAnnualReviewKycIdentityVariables extends AddKycIdentityVariables {
  annualReviewId: string
}

export interface DeleteAnnualReviewKycIdentityData {
  deleteAnnualReviewKycIdentity: Array<AnnualReviewKycIdentityDTO>
}

export interface DeleteAnnualReviewKycIdentityVariables {
  id: string
}

export interface EditAnnualReviewKycIdentityData {
  updateAnnualReviewKycIdentity: AnnualReviewKycIdentityDTO
}

export interface EditAnnualReviewKycIdentityVariables extends EditKycIdentityVariables {
  annualReviewId: string
}

export interface ArchiveAnnualReviewsData {
  archiveAnnualReviews: boolean
}

export interface ArchiveAnnualReviewsVariables {
  ids: Array<string>
}

export interface ResetToNewAnnualReviewsData {
  resetToNewAnnualReviews: boolean
}

export interface ResetToNewAnnualReviewsVariables {
  ids: Array<string>
}

export interface UpdateAnnualReviewDebtorRiskScoresData {
  updateAnnualReviewDebtorRiscScores: AnnualReviewDebtorDetailsDTO
}

export interface UpdateAnnualReviewDebtorRiskScoresVariables {
  id: string
}

export enum AnnualReviewActionFormFields {
  COMMENT = 'comment',
  COMMENT_TYPE = 'commentType',
}

export interface ActionFormValues {
  comment: string
  commentType: CommentType
}

export interface SuggestNewFinancingLimitData {
  suggestAnnualReviewFinancingLimit: DebtorDetailsDTO
}

export interface SuggestNewFinancingLimitFormValues {
  suggestedFinancingLimit: number
}

export interface SuggestNewFinancingLimitVariables extends SuggestNewFinancingLimitFormValues {
  annualReviewId: string
}

export enum SuggestNewFinancingLimitFieldNames {
  NEW_FINANCING_LIMIT = 'suggestedFinancingLimit',
}

export interface AnnualReviewContractListDTO {
  sourcingAgentAdministrates: AdministrationTypes
  administrationFee: number
  annualReviewId: string
  currency: Currency
  duration: number
  endDate: string
  factoringFee: number
  financingLimit: number
  fundingLine: FundingLine
  id: string
  contractName: string
  paymentTerm: number
  product: FactoringProduct
  signingDate: string
  startDate: string
  contractTypeName: string
  contractTypeId: string
  contractStatus: AnnualReviewContractStatus

  [key: string]: unknown
}

export interface Page_AnnualReviewContractListDTO {
  content: Array<AnnualReviewContractListDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface AnnualReviewContractsData {
  annualReviewContracts: Page_AnnualReviewContractListDTO
}

export interface AnnualReviewContractsVariables {
  annualReviewId: string
  contractName?: string
  sourcingAgentAdministrates?: Array<AdministrationTypes>
  contractStatus?: Array<AnnualReviewContractStatus>
  product?: Array<FactoringProduct>
  fundingLine?: Array<FundingLine>
  startDate?: FromToFilterInput
  endDate?: FromToFilterInput
  duration?: MinMaxFilterInput
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface ImportAllAnnualReviewContractFromFabData {
  importAllAnnualReviewContractFromFab: Array<{ id: string }>
}

export interface ImportAllAnnualReviewContractFromFabVariables {
  annualReviewId: string
}

export enum AnnualReviewContractStatus {
  ACTIVE = 'ACTIVE',
  ARCHIVED = 'ARCHIVED',
  EXPIRED = 'EXPIRED',
  EXPIRING_SOON = 'EXPIRING_SOON',
  NEW = 'NEW',
  RENEWED = 'RENEWED',
}

export interface ContractClientDetails {
  beneficiary: string
  correspondenceCountry: string
  correspondenceEmail: string
  correspondenceName: string
  correspondencePhone: string
  correspondencePlace: string
  correspondencePostalCode: string
  correspondenceStreetLine1: string
  correspondenceStreetLine2: string
  iban: string
  legalRepresentative: string
  legalRepresentativeRole: string
  legalStructure: string
  legalStructureId: string
  registrationNumber: string
  vatNumber: string
}

export enum AnnualReviewAuditActions {
  AR_CONTRACT_CLIENT_ADDRESS_UPDATED = 'AR_CONTRACT_CLIENT_ADDRESS_UPDATED',
  AR_CONTRACT_FEE_DETAILS_UPDATED = 'AR_CONTRACT_FEE_DETAILS_UPDATED',
  AR_CONTRACT_IMPORTED = 'AR_CONTRACT_IMPORTED',
  AR_CONTRACT_SYNCED_TO_FAB = 'AR_CONTRACT_SYNCED_TO_FAB',
}

export interface AnnualReviewContractLogDTO {
  id: string
  userFullName: string
  createdAt: Date
  action: AnnualReviewAuditActions
}

export interface AnnualReviewContractDetailsDTO {
  annualReviewId: string
  companyName: string
  clientUsesGAmount: boolean
  appliedFactoringModel: string
  contractFeeDetails: ContractFeeDetailsDTO
  contractName: string
  contractStatus: AnnualReviewContractStatus
  contractTypeId: string
  contractTypeName: string
  fabClientDetails: ClientCompanyDetails
  fabContractGroupId: string
  id: string
  sourcingAgentAdministrates: AdministrationTypes
  sourcingAgentId: string
  syncedWithFab: boolean
  logs: Array<AnnualReviewContractLogDTO>
}

export interface AnnualReviewClientContractData {
  annualReviewContract: AnnualReviewContractDetailsDTO
}

export interface AnnualReviewClientContractVariables {
  id: string
}

export interface AnnualReviewClientCompanyDetails extends ClientCompanyDetailsDTO {
  sourcingAgentAdministrates: string
}

export interface UpdateAnnualReviewContractData {
  saveAnnualReviewContractFeeDetails: AnnualReviewContractDetailsDTO
}

export interface UpdateAnnualReviewContractVariables extends ContractFeeDetailsDTO {
  contractId: string
}

export interface UpdateAnnualReviewClientCompanyDetailsData {
  updateAnnualReviewContractClientAddress: AnnualReviewContractDetailsDTO
}

export interface UpdateAnnualReviewClientCompanyVariables {
  id: string
  correspondenceStreetLine1: string
  correspondenceStreetLine2: string
  correspondencePlace: string
  correspondencePostalCode: string
}

export enum GenerateAnnualReviewContractAddendumFieldNames {
  SIGNING_DATE = 'signingDate',
  DOCUMENT_LANGUAGE = 'documentLanguage',
  SUPPORTING_DOCUMENTS = 'supportingDocuments',
}

export interface GenerateAnnualReviewContractAddendumFormValues {
  [GenerateAnnualReviewContractAddendumFieldNames.SIGNING_DATE]: string
  [GenerateAnnualReviewContractAddendumFieldNames.DOCUMENT_LANGUAGE]: string
  [GenerateAnnualReviewContractAddendumFieldNames.SUPPORTING_DOCUMENTS]: string
}

export interface GenerateAnnualReviewContractAddendumData {
  generateAnnualReviewContractAddendum: ReportFileDTOInput
}

export interface GenerateAnnualReviewContractAddendumVariables {
  documentLanguage: string
  signingDate: string
  contractId: string
  supportingDocuments?: string
}

export enum SyncAnnualReviewContractToFabFieldNames {
  CREATE_RENEWAL_SETTLEMENT = 'createRenewalSettlement',
}

export interface SyncAnnualReviewContractToFabFormValues {
  [SyncAnnualReviewContractToFabFieldNames.CREATE_RENEWAL_SETTLEMENT]: boolean
}

export interface SyncAnnualReviewContractToFabVariables {
  contractId: string
  createRenewalSettlement: boolean
  renewalAmount?: number
}

export interface SyncAnnualReviewContractToFabData {
  syncAnnualReviewContractToFab: { id: string }
}
